import { ColumnEditorOptions } from "primereact/column";
import { Button } from "primereact/button";
import { useState } from "react";

interface BooleanEditorProps {
    options: ColumnEditorOptions;
    onCommit?: (value: unknown) => void;
}

export const BooleanEditor = ({ options, onCommit }: BooleanEditorProps) => {
    // Seed once from the editor's initial value and track the choice locally, so
    // the existing answer shows on open and survives PrimeReact's re-renders.
    const [value, setValue] = useState(() => options.value);

    const handleSelect = (selected: boolean) => {
        setValue(selected);
        options.editorCallback?.(selected);
        // A yes/no pick saves on selection rather than waiting for the cell to blur.
        onCommit?.(selected);
    };

    const isYes = value === true || value === "true" || value === "YES";
    const isNo = value === false || value === "false" || value === "NO";

    return (
        <div className="flex gap-2">
            <Button
                label="YES"
                size="small"
                severity={isYes ? "success" : "secondary"}
                outlined={!isYes}
                onClick={() => handleSelect(true)}
            />
            <Button
                label="NO"
                size="small"
                severity={isNo ? "danger" : "secondary"}
                outlined={!isNo}
                onClick={() => handleSelect(false)}
            />
        </div>
    );
};

// Title case to match the editable tab's display (the legacy table stores
// "Yes"/"No"); case-insensitive so a stored "YES"/"yes" renders consistently.
export const booleanDisplayTemplate = (value: unknown): string => {
    const normalised = typeof value === "string" ? value.trim().toLowerCase() : value;
    if (normalised === true || normalised === "true" || normalised === "yes") {
        return "Yes";
    }
    if (normalised === false || normalised === "false" || normalised === "no") {
        return "No";
    }
    return String(value ?? "");
};
